<script setup>
import { ref, computed, onBeforeUnmount, onMounted } from 'vue';

const props = defineProps({
    schedulesUrl: { type: String, required: true },
    storeUrl: { type: String, required: true },
    baseUrl: { type: String, required: true },
});

const schedules = ref([]);
const loading = ref(true);
const showForm = ref(false);
const editingId = ref(null);
const formStep = ref(1);
const saving = ref(false);
const rootEl = ref(null);

const form = ref({
    specialist_key: '',
    name: '',
    schedule_days: [],
    schedule_time: '07:00',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Oslo',
    config: {
        content_type: 'social_media',
        theme: '',
        tone: '',
        audience: '',
        language: '',
        question: '',
    },
});

const specialists = [
    { key: 'leo', bgClass: 'bg-aiAgentAutomationExpert', cardHoverClass: 'hover:bg-aiAgentAutomationExpert/50', iconColor: 'text-gray-800', dotColor: 'bg-aiAgentAutomationExpert', available: false },
    { key: 'maya', bgClass: 'bg-aiAgentContentCreator', cardHoverClass: 'hover:bg-aiAgentContentCreator/50', iconColor: 'text-gray-800', dotColor: 'bg-aiAgentContentCreator', available: true },
    { key: 'tom', bgClass: 'bg-aiAgentRelationshipManager', cardHoverClass: 'hover:bg-aiAgentRelationshipManager/50', iconColor: 'text-gray-800', dotColor: 'bg-aiAgentRelationshipManager', available: false },
    { key: 'oscar', bgClass: 'bg-aiAgentLeadFinder', cardHoverClass: 'hover:bg-aiAgentLeadFinder/50', iconColor: 'text-gray-800', dotColor: 'bg-aiAgentLeadFinder', available: false },
    { key: 'susan', bgClass: 'bg-aiAgentDataAnalyst', cardHoverClass: 'hover:bg-aiAgentDataAnalyst/50', iconColor: 'text-gray-800', dotColor: 'bg-aiAgentDataAnalyst', available: true },
    { key: 'alex', bgClass: 'bg-aiAgentNewsletterSpecialist', cardHoverClass: 'hover:bg-aiAgentNewsletterSpecialist/50', iconColor: 'text-gray-800', dotColor: 'bg-aiAgentNewsletterSpecialist', available: false },
];

const specialistAvatarComponentNames = {
    leo: 'ai-agent-automation-expert-avatar',
    maya: 'ai-agent-content-creator-avatar',
    tom: 'ai-agent-relationship-manager-avatar',
    oscar: 'ai-agent-lead-finder-avatar',
    susan: 'ai-agent-data-analyst-avatar',
    alex: 'ai-agent-newsletter-specialist-avatar',
};

const days = [
    { key: 'monday', label: () => _mft('dashboard:workSchedules.mon') },
    { key: 'tuesday', label: () => _mft('dashboard:workSchedules.tue') },
    { key: 'wednesday', label: () => _mft('dashboard:workSchedules.wed') },
    { key: 'thursday', label: () => _mft('dashboard:workSchedules.thu') },
    { key: 'friday', label: () => _mft('dashboard:workSchedules.fri') },
    { key: 'saturday', label: () => _mft('dashboard:workSchedules.sat') },
    { key: 'sunday', label: () => _mft('dashboard:workSchedules.sun') },
];

const dayKeyToFull = {
    monday: () => _mft('dashboard:workSchedules.monday'),
    tuesday: () => _mft('dashboard:workSchedules.tuesday'),
    wednesday: () => _mft('dashboard:workSchedules.wednesday'),
    thursday: () => _mft('dashboard:workSchedules.thursday'),
    friday: () => _mft('dashboard:workSchedules.friday'),
    saturday: () => _mft('dashboard:workSchedules.saturday'),
    sunday: () => _mft('dashboard:workSchedules.sunday'),
};

const contentTypes = [
    { value: 'social_media', label: 'Social media post' },
    { value: 'blog_post', label: 'Blog post' },
    { value: 'newsletter', label: 'Newsletter' },
    { value: 'linkedin', label: 'LinkedIn post' },
];

const specialistForKey = (key) => specialists.find(s => s.key === key);
const specialistAvatarForKey = (key) => specialistAvatarComponentNames[key] || null;

// Build the upcoming 7-day timeline from active schedules
const upcomingTimeline = computed(() => {
    const activeSchedules = schedules.value.filter(s => s.is_active);
    if (!activeSchedules.length) return [];

    const now = new Date();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const entries = [];

    for (let offset = 0; offset < 7; offset++) {
        const date = new Date(now);
        date.setDate(date.getDate() + offset);
        const dayKey = dayNames[date.getDay()];

        const matchingSchedules = activeSchedules.filter(
            s => s.schedule_days && s.schedule_days.includes(dayKey)
        );

        if (!matchingSchedules.length) continue;

        let dayLabel;
        if (offset === 0) {
            dayLabel = _mft('dashboard:workSchedules.today');
        } else if (offset === 1) {
            dayLabel = _mft('dashboard:workSchedules.tomorrow');
        } else {
            dayLabel = dayKeyToFull[dayKey] ? dayKeyToFull[dayKey]() : dayKey;
        }

        const dateStr = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

        entries.push({
            offset,
            dayKey,
            dayLabel,
            dateStr,
            isToday: offset === 0,
            isTomorrow: offset === 1,
            runs: matchingSchedules.map(s => ({
                scheduleId: s.id,
                specialistKey: s.specialist_key,
                specialist: specialistForKey(s.specialist_key),
                scheduleName: s.name,
                time: s.schedule_time,
                theme: s.config?.theme || s.config?.question || '',
            })).sort((a, b) => a.time.localeCompare(b.time)),
        });
    }

    return entries;
});

const activeCount = computed(() => schedules.value.filter(s => s.is_active).length);

const formatDays = (scheduleDays) => {
    if (!scheduleDays || !scheduleDays.length) return '';
    return scheduleDays.map(d => {
        const day = days.find(dd => dd.key === d);
        return day ? day.label() : d;
    }).join(', ');
};

const fetchSchedules = async () => {
    loading.value = true;
    try {
        const response = await fetch(props.schedulesUrl);
        schedules.value = await response.json();
    } catch (e) {
        console.error('Failed to fetch schedules', e);
    } finally {
        loading.value = false;
    }
};

const resetForm = () => {
    form.value = {
        specialist_key: '',
        name: '',
        schedule_days: [],
        schedule_time: '07:00',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Oslo',
        config: { content_type: 'social_media', theme: '', tone: '', audience: '', language: '', question: '' },
    };
    formStep.value = 1;
    editingId.value = null;
};

const openCreateForm = () => {
    resetForm();
    showForm.value = true;
};

const openCreateFormFromQuickAction = () => {
    if (!rootEl.value || rootEl.value.offsetParent === null) {
        return;
    }

    rootEl.value.scrollIntoView({ behavior: 'smooth', block: 'start' });
    openCreateForm();
};

const closeFormModal = () => {
    showForm.value = false;
    resetForm();
};

const openEditForm = (schedule) => {
    editingId.value = schedule.id;
    form.value = {
        specialist_key: schedule.specialist_key,
        name: schedule.name,
        schedule_days: [...schedule.schedule_days],
        schedule_time: schedule.schedule_time,
        timezone: schedule.timezone,
        config: { ...schedule.config },
    };
    formStep.value = 2;
    showForm.value = true;
};

const selectSpecialist = (specialist) => {
    if (!specialist.available) return;
    form.value.specialist_key = specialist.key;
    formStep.value = 2;
};

const toggleDay = (dayKey) => {
    const idx = form.value.schedule_days.indexOf(dayKey);
    if (idx === -1) {
        form.value.schedule_days.push(dayKey);
    } else {
        form.value.schedule_days.splice(idx, 1);
    }
};

const saveSchedule = async () => {
    saving.value = true;
    try {
        const url = editingId.value
            ? `${props.baseUrl}/${editingId.value}`
            : props.storeUrl;
        const method = editingId.value ? 'PUT' : 'POST';

        const response = await fetch(url, {
            method,
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
            },
            body: JSON.stringify(form.value),
        });

        if (response.ok) {
            closeFormModal();
            await fetchSchedules();
        }
    } catch (e) {
        console.error('Failed to save schedule', e);
    } finally {
        saving.value = false;
    }
};

const toggleActive = async (schedule) => {
    try {
        const response = await fetch(`${props.baseUrl}/${schedule.id}/toggle`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
            },
        });
        if (response.ok) {
            const updated = await response.json();
            const idx = schedules.value.findIndex(s => s.id === schedule.id);
            if (idx !== -1) schedules.value[idx] = updated;
        }
    } catch (e) {
        console.error('Failed to toggle schedule', e);
    }
};

const deleteSchedule = async (schedule) => {
    if (!confirm(_mft('dashboard:workSchedules.confirmDelete'))) return;
    try {
        await fetch(`${props.baseUrl}/${schedule.id}`, {
            method: 'DELETE',
            headers: {
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
            },
        });
        await fetchSchedules();
    } catch (e) {
        console.error('Failed to delete schedule', e);
    }
};

onMounted(() => {
    fetchSchedules();
    window.addEventListener('open-work-schedule-modal', openCreateFormFromQuickAction);
});

onBeforeUnmount(() => {
    window.removeEventListener('open-work-schedule-modal', openCreateFormFromQuickAction);
});
</script>

<template>
    <div ref="rootEl" data-work-schedules class="space-y-4">
        <!-- Create/Edit Form -->
        <modal-basic
            :open="showForm"
            :showClose="true"
            @closed="closeFormModal"
        >
            <template #header>
                {{ formStep === 1 ? _mft('dashboard:workSchedules.pickSpecialist') : (editingId ? _mft('dashboard:workSchedules.editSchedule') : _mft('dashboard:workSchedules.createSchedule')) }}
            </template>
            <template #body>
                <div class="@container">
                    <!-- Step 1: Pick specialist -->
                    <div v-if="formStep === 1">
                        <div class="grid grid-cols-2 gap-3 @lg:grid-cols-3">
                            <button
                                v-for="specialist in specialists"
                                :key="specialist.key"
                                type="button"
                                @click="selectSpecialist(specialist)"
                                class="group flex min-h-[180px] flex-col justify-between rounded-xl border p-4 text-center transition-all duration-200"
                                :class="[
                                    specialist.available
                                        ? ['cursor-pointer bg-white hover:border-gray-200 hover:shadow-sm', specialist.cardHoverClass]
                                        : 'cursor-not-allowed bg-gray-50/70 opacity-60',
                                    form.specialist_key === specialist.key
                                        ? 'border-wlPrimary ring-2 ring-wlPrimary/15 shadow-sm'
                                        : 'border-gray-100',
                                ]"
                            >
                                <div class="flex flex-col items-center">
                                    <div
                                        class="flex items-center justify-center w-12 h-12 mb-3 rounded-full transition-all duration-300"
                                        :class="[specialist.bgClass, specialist.available ? 'group-hover:scale-125' : '']"
                                    >
                                        <component
                                            v-if="specialistAvatarForKey(specialist.key)"
                                            :is="specialistAvatarForKey(specialist.key)"
                                            class="w-full h-full"
                                        />
                                    </div>
                                    <h4 class="text-sm font-bold text-gray-800">{{ _mft('dashboard:aiSpecialists.' + specialist.key + '.name') }}</h4>
                                    <p class="text-xs text-gray-500">{{ _mft('dashboard:aiSpecialists.' + specialist.key + '.role') }}</p>
                                    <p class="mt-1 text-xs italic text-gray-400">{{ _mft('dashboard:aiSpecialists.' + specialist.key + '.tagline') }}</p>
                                </div>
                                <span
                                    v-if="!specialist.available"
                                    class="inline-block px-2 py-0.5 mt-3 text-xs font-medium text-gray-400 bg-gray-100 rounded-full"
                                >
                                    {{ _mft('dashboard:workSchedules.comingSoon') }}
                                </span>
                            </button>
                        </div>
                        <div class="flex justify-end mt-4">
                            <button-default @click="closeFormModal" size="xs">
                                {{ _mft('dashboard:workSchedules.cancel') }}
                            </button-default>
                        </div>
                    </div>

                    <!-- Step 2: Configure -->
                    <div v-if="formStep === 2">
                        <div class="space-y-4">
                            <div>
                                <label class="block mb-1 text-xs font-medium text-gray-600">{{ _mft('dashboard:workSchedules.scheduleName') }}</label>
                                <input
                                    v-model="form.name"
                                    type="text"
                                    class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-wlPrimary/30 focus:border-wlPrimary"
                                    :placeholder="_mft('dashboard:workSchedules.scheduleNamePlaceholder')"
                                />
                            </div>

                            <div>
                                <label class="block mb-1 text-xs font-medium text-gray-600">{{ _mft('dashboard:workSchedules.days') }}</label>
                                <div class="flex flex-wrap gap-1.5">
                                    <component
                                        v-for="day in days"
                                        :key="day.key"
                                        :is="form.schedule_days.includes(day.key) ? 'button-primary' : 'button-default'"
                                        @click="toggleDay(day.key)"
                                        size="xs"
                                    >
                                        {{ day.label() }}
                                    </component>
                                </div>
                            </div>

                            <div>
                                <label class="block mb-1 text-xs font-medium text-gray-600">{{ _mft('dashboard:workSchedules.time') }}</label>
                                <input
                                    v-model="form.schedule_time"
                                    type="time"
                                    class="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-wlPrimary/30 focus:border-wlPrimary"
                                />
                            </div>

                            <template v-if="form.specialist_key === 'maya'">
                                <div>
                                    <label class="block mb-1 text-xs font-medium text-gray-600">{{ _mft('dashboard:workSchedules.contentType') }}</label>
                                    <select
                                        v-model="form.config.content_type"
                                        class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-wlPrimary/30 focus:border-wlPrimary"
                                    >
                                        <option v-for="ct in contentTypes" :key="ct.value" :value="ct.value">{{ ct.label }}</option>
                                    </select>
                                </div>
                                <div>
                                    <label class="block mb-1 text-xs font-medium text-gray-600">{{ _mft('dashboard:workSchedules.theme') }}</label>
                                    <textarea
                                        v-model="form.config.theme"
                                        rows="3"
                                        class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-wlPrimary/30 focus:border-wlPrimary resize-none"
                                        :placeholder="_mft('dashboard:workSchedules.themePlaceholder')"
                                    ></textarea>
                                </div>
                                <div>
                                    <label class="block mb-1 text-xs font-medium text-gray-600">{{ _mft('dashboard:workSchedules.tone') }}</label>
                                    <input
                                        v-model="form.config.tone"
                                        type="text"
                                        class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-wlPrimary/30 focus:border-wlPrimary"
                                        :placeholder="_mft('dashboard:businessContext.tone.friendly')"
                                    />
                                </div>
                                <div>
                                    <label class="block mb-1 text-xs font-medium text-gray-600">{{ _mft('dashboard:workSchedules.audience') }}</label>
                                    <input
                                        v-model="form.config.audience"
                                        type="text"
                                        class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-wlPrimary/30 focus:border-wlPrimary"
                                        :placeholder="_mft('dashboard:businessContext.audience.placeholder')"
                                    />
                                </div>
                                <div>
                                    <label class="block mb-1 text-xs font-medium text-gray-600">{{ _mft('dashboard:workSchedules.language') }}</label>
                                    <input
                                        v-model="form.config.language"
                                        type="text"
                                        class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-wlPrimary/30 focus:border-wlPrimary"
                                        :placeholder="_mft('dashboard:businessContext.language.english')"
                                    />
                                </div>
                            </template>

                            <template v-if="form.specialist_key === 'susan'">
                                <div>
                                    <label class="block mb-1 text-xs font-medium text-gray-600">{{ _mft('dashboard:workSchedules.susanQuestion') }}</label>
                                    <textarea
                                        v-model="form.config.question"
                                        rows="3"
                                        class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-wlPrimary/30 focus:border-wlPrimary resize-none"
                                        :placeholder="_mft('dashboard:workSchedules.susanQuestionPlaceholder')"
                                    ></textarea>
                                </div>
                            </template>

                            <div class="flex justify-end gap-2 pt-2">
                                <button-default
                                    v-if="!editingId"
                                    @click="formStep = 1"
                                    size="xs"
                                >
                                    {{ _mft('dashboard:businessContext.nav.back') }}
                                </button-default>
                                <button-default
                                    @click="closeFormModal"
                                    size="xs"
                                >
                                    {{ _mft('dashboard:workSchedules.cancel') }}
                                </button-default>
                                <button-primary
                                    @click="saveSchedule"
                                    :disabled="saving || !form.name || !form.schedule_days.length"
                                    size="sm"
                                >
                                    {{ _mft('dashboard:workSchedules.save') }}
                                </button-primary>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </modal-basic>

        <!-- Loading -->
        <div v-if="loading" class="py-8 text-center text-sm text-gray-400">
            <svg class="w-5 h-5 mx-auto animate-spin text-gray-300" viewBox="0 0 24 24" fill="none">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
        </div>

        <!-- Empty state -->
        <card-basic v-else-if="schedules.length === 0" class="text-center">
            <div class="flex items-center justify-center w-14 h-14 mx-auto mb-4 rounded-full bg-aiAgentContentCreator/40">
                <svg class="w-7 h-7 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                    <circle cx="12" cy="16" r="1" fill="currentColor" />
                    <circle cx="8" cy="16" r="1" fill="currentColor" />
                    <circle cx="16" cy="16" r="1" fill="currentColor" />
                </svg>
            </div>
            <h3 class="text-sm font-semibold text-gray-700">{{ _mft('dashboard:workSchedules.emptyTitle') }}</h3>
            <p class="mt-1 text-xs text-gray-500 max-w-[220px] mx-auto leading-relaxed">{{ _mft('dashboard:workSchedules.emptyDescription') }}</p>
            <button-base
                size="sm"
                style-type="secondary"
                @click="openCreateForm"
            >
                <template #icon>
                    <span aria-hidden="true">
                        <i class="fa-regular fa-plus text-xxs"></i>
                    </span>
                </template>
                {{ _mft('dashboard:workSchedules.addSchedule') }}
            </button-base>
        </card-basic>

        <!-- Content when schedules exist -->
        <template v-else-if="!loading">
            <!-- Upcoming week timeline -->
            <card-basic v-if="upcomingTimeline.length" :compact="true">
                <div class="pb-3 border-b border-gray-50 flex items-center justify-between">
                    <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wider">{{ _mft('dashboard:workSchedules.upcomingWork') }}</h3>
                    <span class="text-xs text-gray-400">{{ activeCount }} {{ _mft('dashboard:workSchedules.active').toLowerCase() }}</span>
                </div>

                <div class="divide-y divide-gray-50 @container">
                    <div
                        v-for="entry in upcomingTimeline"
                        :key="entry.dayKey + entry.offset"
                        class="block @sm:flex gap-3 py-3"
                        :class="{ 'bg-wlPrimary/[0.02]': entry.isToday }"
                    >
                        <!-- Day column -->
                        <div class="flex-shrink-0 w-16 pt-0.5">
                            <span
                                class="text-xs font-semibold block"
                                :class="entry.isToday ? 'text-wlPrimary' : 'text-gray-700'"
                            >{{ entry.dayLabel }}</span>
                            <span class="text-xs text-gray-400">{{ entry.dateStr }}</span>
                        </div>

                        <!-- Runs for this day -->
                        <div class="flex-1 space-y-2">
                            <div
                                v-for="(run, ri) in entry.runs"
                                :key="ri"
                                class="flex items-center gap-3 px-3 py-2 border border-gray-100 rounded-xl bg-gray-50/60 relative"
                            >
                                <div
                                    class="flex items-center justify-center flex-shrink-0 w-10 h-10 rounded-full"
                                    :class="run.specialist?.bgClass || 'bg-gray-100'"
                                >
                                    <component
                                        v-if="specialistAvatarForKey(run.specialistKey)"
                                        :is="specialistAvatarForKey(run.specialistKey)"
                                        class="w-full h-full"
                                    />
                                </div>

                                <div class="flex-1 min-w-0">
                                    <div class="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                        <span class="text-sm font-bold text-gray-800 truncate">
                                            {{ _mft('dashboard:aiSpecialists.' + run.specialistKey + '.name') }}
                                        </span>
                                        <span class="text-xs text-gray-500 truncate">
                                            {{ _mft('dashboard:aiSpecialists.' + run.specialistKey + '.role') }}
                                        </span>
                                    </div>
                                    <p class="text-xs text-gray-500 truncate mb-0.5">{{ run.scheduleName }}</p>
                                    <p v-if="run.theme" class="mt-0.5 text-xs italic text-gray-400 truncate">{{ run.theme }}</p>
                                </div>

                                <span class="flex-shrink-0 px-2 py-0.5 text-xs font-medium text-gray-500 bg-gray-50 rounded-full absolute top-1.5 right-0 lg:static">
                                    {{ run.time }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- If today has no runs but other days do, show a subtle indicator -->
                <div
                    v-if="upcomingTimeline.length && !upcomingTimeline[0]?.isToday"
                    class="px-4 pb-2 text-xs text-gray-400 text-center"
                >
                    {{ _mft('dashboard:workSchedules.nextRun') }}: {{ upcomingTimeline[0]?.dayLabel }} {{ _mft('dashboard:workSchedules.at') }} {{ upcomingTimeline[0]?.runs[0]?.time }}
                </div>

                <div class="flex justify-center">
                    <button-base
                        size="sm"
                        style-type="secondary"
                        @click="openCreateForm"
                    >
                        <template #icon>
                            <span aria-hidden="true">
                                <i class="fa-regular fa-plus text-xxs"></i>
                            </span>
                        </template>
                        {{ _mft('dashboard:workSchedules.addSchedule') }}
                    </button-base>
                </div>
            </card-basic>

            <!-- No upcoming runs message -->
            <card-basic v-else-if="schedules.length && !upcomingTimeline.length" :compact="true">
                <p class="text-xs text-gray-400 text-center">{{ _mft('dashboard:workSchedules.noUpcoming') }}</p>
                <div class="flex justify-center">
                    <button-base
                        size="sm"
                        style-type="secondary"
                        @click="openCreateForm"
                    >
                        <template #icon>
                            <span aria-hidden="true">
                                <i class="fa-regular fa-plus text-xxs"></i>
                            </span>
                        </template>
                        {{ _mft('dashboard:workSchedules.addSchedule') }}
                    </button-base>
                </div>
            </card-basic>

            <!-- Schedule roster -->
            <card-basic>
                <div class="pb-3 border-b border-gray-50">
                    <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wider">{{ _mft('dashboard:workSchedules.schedules') }}</h3>
                </div>

                <div class="divide-y divide-gray-50">
                    <div
                        v-for="schedule in schedules"
                        :key="schedule.id"
                        class="group py-3"
                    >
                        <div class="flex items-center gap-3">
                            <div
                                class="flex items-center justify-center flex-shrink-0 w-10 h-10 rounded-full"
                                :class="specialistForKey(schedule.specialist_key)?.bgClass || 'bg-gray-100'"
                            >
                                <component
                                    v-if="specialistAvatarForKey(schedule.specialist_key)"
                                    :is="specialistAvatarForKey(schedule.specialist_key)"
                                    class="w-full h-full"
                                />
                            </div>

                            <div class="flex-1 min-w-0">
                                <div class="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                    <span class="text-sm font-medium text-gray-800 truncate">{{ schedule.name }}</span>
                                    <span
                                        class="flex-shrink-0 inline-block w-1.5 h-1.5 rounded-full"
                                        :class="schedule.is_active ? 'bg-green-400' : 'bg-gray-300'"
                                    ></span>
                                </div>
                                <div class="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                                    <span class="text-xs font-bold text-gray-800 truncate">
                                        {{ _mft('dashboard:aiSpecialists.' + schedule.specialist_key + '.name') }}
                                    </span>
                                    <span class="text-xs text-gray-500 truncate">
                                        {{ _mft('dashboard:aiSpecialists.' + schedule.specialist_key + '.role') }}
                                    </span>
                                </div>
                                <div class="flex items-center gap-1 mt-1">
                                    <div
                                        v-for="day in days"
                                        :key="day.key"
                                        class="flex items-center justify-center w-5 h-5 rounded text-xs leading-none"
                                        :class="schedule.schedule_days?.includes(day.key)
                                            ? (specialistForKey(schedule.specialist_key)?.bgClass || 'bg-gray-200') + ' font-semibold ' + (specialistForKey(schedule.specialist_key)?.iconColor || 'text-gray-600')
                                            : 'text-gray-300'"
                                        :title="day.label()"
                                    >{{ day.label().charAt(0) }}</div>
                                    <span class="ml-1 text-xs text-gray-400">{{ schedule.schedule_time }}</span>
                                </div>
                            </div>

                            <!-- Hover actions -->
                            <div class="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button-base
                                    @click="toggleActive(schedule)"
                                    style-type="default"
                                    size="xs"
                                    class="!border-0 !bg-transparent !px-1.5 !py-1.5 !text-gray-400"
                                    :title="schedule.is_active ? _mft('dashboard:workSchedules.paused') : _mft('dashboard:workSchedules.active')"
                                >
                                    <template #icon>
                                        <svg v-if="schedule.is_active" class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                            <rect x="6" y="4" width="4" height="16" />
                                            <rect x="14" y="4" width="4" height="16" />
                                        </svg>
                                        <svg v-else class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                            <polygon points="5 3 19 12 5 21 5 3" />
                                        </svg>
                                    </template>
                                </button-base>
                                <button-base
                                    @click="openEditForm(schedule)"
                                    style-type="default"
                                    size="xs"
                                    class="!border-0 !bg-transparent !px-1.5 !py-1.5 !text-gray-400"
                                >
                                    <template #icon>
                                        <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                        </svg>
                                    </template>
                                </button-base>
                                <button-base
                                    @click="deleteSchedule(schedule)"
                                    style-type="default"
                                    size="xs"
                                    class="!border-0 !bg-transparent !px-1.5 !py-1.5 !text-red-500"
                                >
                                    <template #icon>
                                        <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                            <polyline points="3 6 5 6 21 6" />
                                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                        </svg>
                                    </template>
                                </button-base>
                            </div>
                        </div>
                    </div>
                </div>
            </card-basic>
        </template>
    </div>
</template>
